import React from 'react';
import { HealerTrinketAnalysisData, HealerTrinketFightRow } from '../types';
import { TbcBreakdownColumn } from './components/TbcFightBreakdownTable';
import { aggregateHealerTrinketBossGroup, formatBossAggregateDuration } from './healerTrinketAggregates';
import { formatDuration, formatNumber, isBossWipe } from './utils';

function formatIntSpirit(intellect: number | null, spirit: number | null): string {
    const intLabel = intellect !== null ? formatNumber(intellect, 0) : '—';
    const spiLabel = spirit !== null ? formatNumber(spirit, 0) : '—';
    return `${intLabel} / ${spiLabel}`;
}

function formatActualTheoretical(actual: string, theoretical: string): string {
    return `${actual} / ${theoretical}`;
}

function renderDurationCell(fight: HealerTrinketFightRow): React.ReactNode {
    const duration = formatDuration(fight.durationSeconds);
    if (!isBossWipe(fight)) {
        return duration;
    }
    return React.createElement(
        React.Fragment,
        null,
        duration,
        React.createElement('span', { className: 'fight-type-badge' }, 'wipe')
    );
}

export function buildHealerTrinketFightColumns(
    result: HealerTrinketAnalysisData
): TbcBreakdownColumn<HealerTrinketFightRow>[] {
    const earringApplicable = result.earring.applicable;

    const aggregateFor = (fights: HealerTrinketFightRow[]) => {
        return aggregateHealerTrinketBossGroup(fights, earringApplicable);
    };

    const columns: TbcBreakdownColumn<HealerTrinketFightRow>[] = [
        {
            key: 'dur',
            header: 'Dur',
            render: renderDurationCell,
            aggregate: (fights) => formatBossAggregateDuration(aggregateFor(fights).totalDurationSeconds),
        },
        {
            key: 'cpm',
            header: 'CPM',
            render: (fight) => formatNumber(fight.cpm, 1),
            aggregate: (fights) => formatNumber(aggregateFor(fights).cpm, 1),
        },
        {
            key: 'intSpi',
            header: 'Int / Spi',
            render: (fight) => formatIntSpirit(fight.intellect, fight.spirit),
            aggregate: (fights) => {
                const agg = aggregateFor(fights);
                return formatIntSpirit(agg.avgIntellect, agg.avgSpirit);
            },
        },
        {
            key: 'bd',
            header: 'BD',
            render: (fight) => (fight.blueDragonEquipped ? 'Yes' : 'No'),
            aggregate: (fights) => aggregateFor(fights).blueDragonEquipped,
        },
        {
            key: 'bdUptime',
            header: 'BD uptime',
            render: (fight) =>
                fight.blueDragonEquipped
                    ? `${formatNumber(fight.blueDragonUptimeSeconds, 1)}s (${formatNumber(fight.blueDragonUptimePercent, 1)}%)`
                    : `th ${formatNumber(fight.blueDragonTheoreticalUptimePercent, 1)}%`,
            aggregate: (fights) => aggregateFor(fights).blueDragonUptime,
        },
        {
            key: 'bdMp5',
            header: 'BD MP5',
            render: (fight) =>
                formatActualTheoretical(
                    fight.blueDragonEquipped ? formatNumber(fight.blueDragonMp5, 1) : '—',
                    formatNumber(fight.blueDragonTheoreticalMp5, 1)
                ),
            aggregate: (fights) => {
                const agg = aggregateFor(fights);
                return formatActualTheoretical(agg.blueDragonMp5, agg.blueDragonTheoreticalMp5);
            },
        },
        {
            key: 'eog',
            header: 'EoG',
            render: (fight) => (fight.eyeOfGruulEquipped ? 'Yes' : 'No'),
            aggregate: (fights) => aggregateFor(fights).eyeOfGruulEquipped,
        },
        {
            key: 'eogProcs',
            header: 'EoG procs',
            render: (fight) =>
                fight.eyeOfGruulEquipped
                    ? `${fight.eyeOfGruulConsumed}/${fight.eyeOfGruulProcs}`
                    : `th ${formatNumber(fight.eyeOfGruulTheoreticalProcs, 1)}`,
            aggregate: (fights) => aggregateFor(fights).eyeOfGruulProcs,
        },
        {
            key: 'eogMp5',
            header: 'EoG MP5',
            render: (fight) =>
                formatActualTheoretical(
                    fight.eyeOfGruulEquipped ? formatNumber(fight.eyeOfGruulMp5, 1) : '—',
                    formatNumber(fight.eyeOfGruulTheoreticalMp5, 1)
                ),
            aggregate: (fights) => {
                const agg = aggregateFor(fights);
                return formatActualTheoretical(agg.eyeOfGruulMp5, agg.eyeOfGruulTheoreticalMp5);
            },
        },
    ];

    if (earringApplicable) {
        columns.push(
            {
                key: 'earring',
                header: 'EoSM',
                render: (fight) => (fight.earringEquipped ? 'Yes' : 'No'),
                aggregate: (fights) => aggregateFor(fights).earringEquipped,
            },
            {
                key: 'earringUptime',
                header: 'EoSM uptime',
                render: (fight) =>
                    fight.earringEquipped
                        ? `${formatNumber(fight.earringUptimeSeconds, 1)}s (${formatNumber(fight.earringUptimePercent, 1)}%)`
                        : `th ${formatNumber(fight.earringTheoreticalUptimePercent, 1)}%`,
                aggregate: (fights) => aggregateFor(fights).earringUptime,
            },
            {
                key: 'earringMp5',
                header: 'EoSM MP5',
                render: (fight) =>
                    formatActualTheoretical(
                        fight.earringEquipped ? formatNumber(fight.earringMp5, 1) : '—',
                        formatNumber(fight.earringTheoreticalMp5, 1)
                    ),
                aggregate: (fights) => {
                    const agg = aggregateFor(fights);
                    return formatActualTheoretical(agg.earringMp5, agg.earringTheoreticalMp5);
                },
            }
        );
    }

    return columns;
}
